import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "../../components/Header";
import { tokens } from "../../theme";

const Admins = () => {
  const [data, setData] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [adminsRes, rolesRes] = await Promise.all([
          fetch("https://backend.jcgn.org/admin/getAdmin"),
          fetch("https://backend.jcgn.org/api/adminRole/getAll"),
        ]);
        const adminsData = await adminsRes.json();
        const rolesData = await rolesRes.json();

        const sortedAdmins = adminsData.sort((a, b) => b.id - a.id);
        setData(sortedAdmins);
        setRoles(rolesData.result || []);
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire("Error!", "Failed to load data. Please try again.", "error");
      }
    };

    fetchData();
  }, []);

  const getRoleName = (admin_role_id) => {
    const role = roles.find((r) => r.id === admin_role_id);
    return role ? role.position : "Unknown";
  };

  const handleViewClick = (admin) => {
    setSelectedAdmin(admin);
    setViewOpen(true);
  };

  const handleCloseView = () => {
    setViewOpen(false);
    setSelectedAdmin(null);
  };

  const columns = [
    { field: "id", headerName: "ID", width: 70 },
    {
      field: "image",
      headerName: "Avatar",
      width: 80,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Avatar
            src={params.row.image || undefined}
            alt={params.row.name}
            sx={{ width: 36, height: 36 }}
          >
            {!params.row.image && params.row.name?.charAt(0).toUpperCase()}
          </Avatar>
        </Box>
      ),
    },
    { field: "name", headerName: "Name", flex: 1 },
    { field: "email", headerName: "Email", flex: 1.3 },
    { field: "role", headerName: "Role", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 0.7,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Chip
            label={params.row.status ? "Active" : "Inactive"}
            size="small"
            sx={{
              backgroundColor: params.row.status ? "#4caf50" : "#f44336",
              color: "white",
              fontWeight: "bold",
              fontSize: "11px",
            }}
          />
        </Box>
      ),
    },
    {
      field: "Actions",
      headerName: "Actions",
      flex: 0.4,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" height="100%">
          <Tooltip title="View">
            <IconButton onClick={() => handleViewClick(params.row)}>
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom="-10px"
      >
        <Header title="Admins Management" subtitle="Managing the admins" />
        <Box>
          <Link to={"/addadmin"}>
            <Button
              variant="contained"
              sx={{
                backgroundColor: "#6870fa",
                color: "white",
                fontSize: "16px",
                "&:hover": { backgroundColor: "#3e4396" },
              }}
            >
              Add Admin
            </Button>
          </Link>
        </Box>
      </Box>

      <Box
        m="10px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-root": { border: "none", fontSize: "14px" },
          "& .MuiDataGrid-cell": { borderBottom: "none" },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.greenAccent[700],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.greenAccent[700],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.greenAccent[200]} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${colors.grey[100]} !important`,
          },
        }}
      >
        <DataGrid
          rows={data}
          columns={columns}
          components={{ Toolbar: GridToolbar }}
          rowHeight={56}
        />
      </Box>

      {/* Simple View Popup */}
      <Dialog
        open={viewOpen}
        onClose={handleCloseView}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: colors.primary[400],
            backgroundImage: "none",
            borderRadius: "8px",
          },
        }}
      >
        <DialogTitle
          sx={{
            backgroundColor: colors.greenAccent[700],
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
            px: 2.5,
          }}
        >
          <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
            Admin Details
          </Typography>
          <IconButton
            onClick={handleCloseView}
            size="small"
            sx={{ color: colors.grey[100] }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: "20px !important", px: 3, pb: 3 }}>
          {selectedAdmin && (
            <Box display="flex" flexDirection="column" gap={2}>
              {/* Avatar + Name */}
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Avatar
                  src={selectedAdmin.image || undefined}
                  alt={selectedAdmin.name}
                  sx={{ width: 52, height: 52, fontSize: "1.3rem" }}
                >
                  {!selectedAdmin.image &&
                    selectedAdmin.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold" color={colors.grey[100]}>
                    {selectedAdmin.name}
                  </Typography>
                  <Typography variant="body2" color={colors.grey[400]}>
                    {selectedAdmin.email}
                  </Typography>
                </Box>
              </Box>

              {/* Detail Rows */}
              {[
                { label: "ID", value: selectedAdmin.id },
                { label: "Role", value: selectedAdmin.role },
                {
                  label: "Role Position",
                  value: getRoleName(selectedAdmin.admin_role_id),
                },
                {
                  label: "Status",
                  value: selectedAdmin.status ? "Active" : "Inactive",
                  isChip: true,
                  chipColor: selectedAdmin.status ? "#4caf50" : "#f44336",
                },
              ].map((item) => (
                <Box
                  key={item.label}
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  borderBottom={`1px solid ${colors.grey[700]}`}
                  pb={1}
                >
                  <Typography
                    variant="body2"
                    color={colors.grey[400]}
                    fontWeight="600"
                  >
                    {item.label}
                  </Typography>
                  {item.isChip ? (
                    <Chip
                      label={item.value}
                      size="small"
                      sx={{
                        backgroundColor: item.chipColor,
                        color: "white",
                        fontWeight: "bold",
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color={colors.grey[100]}>
                      {item.value}
                    </Typography>
                  )}
                </Box>
              ))}

              <Button
                fullWidth
                variant="contained"
                onClick={handleCloseView}
                sx={{
                  mt: 1,
                  backgroundColor: "#6870fa",
                  color: "white",
                  "&:hover": { backgroundColor: "#3e4396" },
                }}
              >
                Close
              </Button>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default Admins;